
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Menu, Plus } from "lucide-react";
import { useAuth } from "@/contexts/auth";
import { usePermissions } from "@/hooks/use-permissions";
import UserManagementHeader from "@/components/admin/users/UserManagementHeader";
import UsersTable from "@/components/admin/users/UsersTable";
import EditUserForm from "@/components/admin/users/EditUserForm";
import VerifyUserDialog from "@/components/admin/users/VerifyUserDialog";
import AdminCountryAssignmentDialog from "@/components/admin/users/AdminCountryAssignmentDialog";
import { useUserManagement } from "@/components/admin/users/useUserManagement";
import AdminSidebar from "@/components/admin/AdminSidebar";
import AdminHeader from "@/components/admin/AdminHeader";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import CreateUserDialog from "@/components/admin/users/CreateUserDialog";
import { UserSortOption } from "@/components/admin/users/UserFilters";

const AdminUsers = () => {
  const navigate = useNavigate();
  const { user, profile, isLoading } = useAuth();
  const { isSuperAdmin, isAdmin } = usePermissions();
  const isMobile = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  
  const {
    paginatedUsers,
    totalUsers,
    loading, 
    pagination,
    searchQuery, 
    setSearchQuery,
    filters,
    setFilters,
    sortOption,
    setSortOption,
    editModalOpen,
    setEditModalOpen,
    verifyDialogOpen,
    setVerifyDialogOpen,
    countryAssignmentOpen,
    setCountryAssignmentOpen,
    selectedUser,
    formData,
    setFormData,
    handleEditUser,
    handleVerifyUser,
    handleManageCountries,
    confirmVerifyUser,
    handleUpdateUser,
    createModalOpen,
    setCreateModalOpen,
    handleCreateUser,
    handleCreateUserSubmit,
    handleDeleteUser,
    countries
  } = useUserManagement();
  
  useEffect(() => {
    // Set document title
    document.title = "User Management - NationCard";
    
    console.log("AdminUsers permissions check:", { 
      userEmail: user?.email,
      userRole: profile?.role,
      isAdmin: isAdmin(),
      isSuperAdmin: isSuperAdmin()
    });
    
    // Only perform this check when auth loading is complete
    if (!isLoading) {
      if (!user) {
        console.log("No user found, redirecting to auth");
        toast.error("Please log in to access the admin dashboard");
        navigate("/auth");
        return;
      }
      
      if (!isAdmin() && !isSuperAdmin()) {
        console.log("User lacks admin permissions, redirecting to home");
        toast.error("You do not have permission to access the admin dashboard");
        navigate("/");
        return;
      }
    }
  }, [user, profile, isAdmin, isSuperAdmin, navigate, isLoading]);

  // Effect to handle sidebar state on mobile/desktop switch
  useEffect(() => {
    setSidebarOpen(!isMobile);
  }, [isMobile]);
  
  if (isLoading) {
    return <div className="flex h-screen w-full items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-nation-blue mx-auto mb-4"></div>
        <p className="text-nation-blue">Loading admin dashboard...</p>
      </div>
    </div>;
  }
  
  // Additional check to ensure we have a user with proper permissions before rendering
  if (!user || (!isAdmin() && !isSuperAdmin())) {
    return null; // This will prevent render flashing while redirects happen
  }
  
  const handleSortChange = (value: UserSortOption) => {
    setSortOption(value);
  };
  
  return (
    <div className="flex min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Mobile sidebar toggle */}
      {isMobile && !sidebarOpen && (
        <Button
          variant="outline"
          size="icon"
          className="fixed top-4 left-4 z-40 md:hidden"
          onClick={() => setSidebarOpen(true)}
        >
          <Menu className="h-4 w-4" />
        </Button>
      )}
      
      {/* Sidebar with conditional display for mobile */}
      <div className={`${sidebarOpen ? "block" : "hidden"} ${isMobile ? "fixed inset-0 z-50 bg-black/50" : ""}`} onClick={isMobile ? () => setSidebarOpen(false) : undefined}>
        <div onClick={e => isMobile && e.stopPropagation()} className={isMobile ? "h-full w-64" : ""}>
          <AdminSidebar onClose={isMobile ? () => setSidebarOpen(false) : undefined} />
        </div>
      </div>
      
      <div className="flex-1 flex flex-col overflow-hidden">
        <AdminHeader>
          {isMobile && (
            <Button
              variant="outline"
              size="icon"
              className="mr-2"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="h-4 w-4" />
            </Button>
          )}
          User Management
        </AdminHeader>
        
        <main className="p-4 md:p-6 overflow-x-auto">
          <div className="mb-4">
            <div className="text-sm text-gray-500 mb-1">
              Admin / Users
            </div>
          </div>
          
          {/* Header section with search and filters */}
          <div className="space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div className="w-full md:w-auto">
                <UserManagementHeader 
                  searchQuery={searchQuery} 
                  setSearchQuery={setSearchQuery}
                  totalUsers={totalUsers}
                  countries={countries}
                  filters={filters}
                  setFilters={setFilters}
                  sortOption={sortOption}
                  setSortOption={handleSortChange}
                />
              </div>
              
              <Button 
                onClick={handleCreateUser} 
                className="shadow-md border border-transparent w-full md:w-auto"
              >
                <Plus className="h-4 w-4 mr-1" />
                Create User
              </Button>
            </div>
            
            {/* Table section */}
            <div>
              <UsersTable 
                users={paginatedUsers} 
                loading={loading} 
                onEditUser={handleEditUser} 
                onVerifyUser={handleVerifyUser}
                onDeleteUser={handleDeleteUser}
                onManageCountries={handleManageCountries}
                isMobile={isMobile}
                pagination={pagination}
                totalUsers={totalUsers}
              />
            </div>
          </div>
          
          <EditUserForm 
            open={editModalOpen}
            setOpen={setEditModalOpen}
            selectedUser={selectedUser}
            onUpdateUser={handleUpdateUser}
            formData={formData}
            setFormData={setFormData}
          />
          
          <CreateUserDialog 
            open={createModalOpen}
            setOpen={setCreateModalOpen}
            formData={formData}
            setFormData={setFormData}
            onCreateUser={handleCreateUserSubmit}
            countries={countries}
          />
          
          <VerifyUserDialog 
            open={verifyDialogOpen}
            setOpen={setVerifyDialogOpen}
            selectedUser={selectedUser}
            onVerifyUser={confirmVerifyUser}
          />

          <AdminCountryAssignmentDialog
            open={countryAssignmentOpen}
            onOpenChange={setCountryAssignmentOpen}
            admin={selectedUser}
          />
        </main>
      </div>
    </div>
  );
};

export default AdminUsers;
